/**
 * Generiert Social Media Icons HTML für E-Mail Footer
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers
 * @returns {string} HTML-Code für Social Media Icons
 */
const generateSocialMediaIcons = (userSettings) => {
  // Prüfe ob socialIcons existiert und gefiltere nur Icons mit URL und Link
  const socialIcons = userSettings.socialIcons || [];
  const validIcons = socialIcons.filter(icon => icon.iconUrl && icon.link);
  
  if (validIcons.length === 0) {
    return '';
  }
  
  // Position bestimmen (left, center, right)
  const position = userSettings.socialIconsPosition || 'left';
  let alignment = 'left';
  if (position === 'center') alignment = 'center';
  if (position === 'right') alignment = 'right';
  
  // Margin Top & Bottom Werte
  const marginTop = userSettings.socialIconsMarginTop !== undefined ? userSettings.socialIconsMarginTop : 15;
  const marginBottom = userSettings.socialIconsMarginBottom !== undefined ? userSettings.socialIconsMarginBottom : 10;
  
  return `
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: ${marginTop}px; margin-bottom: ${marginBottom}px; table-layout: auto;">
      <tr>
        <td align="${alignment}" style="text-align: ${alignment};">
          <table cellpadding="0" cellspacing="0" border="0" style="display: inline-block; table-layout: auto;">
            <tr>
              ${validIcons.map((icon, index) => {
                const iconSize = icon.size || 32;
                return `
                <td style="${index < validIcons.length - 1 ? 'padding-right: 12px;' : ''} vertical-align: middle;">
                  <a href="${icon.link}" target="_blank" style="display: inline-block; text-decoration: none; border: 0;">
                    <img src="${icon.iconUrl}" alt="Social" width="${iconSize}" height="${iconSize}" border="0" style="display: block; width: ${iconSize}px; height: ${iconSize}px; border: 0; outline: none; text-decoration: none;" />
                  </a>
                </td>
              `;
              }).join('')}
            </tr>
          </table>
        </td>
      </tr>
    </table>
  `;
};

/**
 * Generiert ein einzelnes Footer-HTML aus Footer-Einstellungen
 * @param {Object} footerSettings - Die Einstellungen für einen einzelnen Footer
 * @returns {string} HTML-Code für den E-Mail-Footer
 */
const generateOptOutHtml = (footerSettings) => {
  if (!footerSettings.optOutEnabled) return '';
  const text = footerSettings.optOutText || 'Unsubscribe';
  return `
    <div style="margin-top: 40px; text-align: left;">
      <a href="{{OPTOUT_URL}}" target="_blank" style="font-family: '${footerSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; font-size: 10px; color: #999; text-decoration: underline; cursor: pointer;">${text}</a>
    </div>
  `;
};

const generateSingleFooter = (footerSettings) => {
  if (!footerSettings) {
    return '';
  }
  
  // Prüfe, ob das Bild neben dem Text angezeigt werden soll
  const isBesideLayout = footerSettings.footer_image_position === 'beside' && footerSettings.footer_image_url;
  
  // Bildgröße aus den Einstellungen oder Standardwert (als Zahl sicherstellen)
  const rawSize = footerSettings.footer_image_size;
  const imageSize = parseInt(rawSize, 10) || 80;
  
  // Generiere Social Media Icons
  const socialMediaHtml = generateSocialMediaIcons(footerSettings);

  // Opt-Out Link
  const optOutHtml = generateOptOutHtml(footerSettings);
  
  if (isBesideLayout) {
    // Layout mit Bild neben dem Text (mit DIVs)
    // Verwende table-basiertes Layout für maximale Kompatibilität
    // Icons sollen bündig mit Text beginnen, nicht unter dem Bild
    const iconOffsetLeft = imageSize + 15; // Bildbreite + padding-right
    
    return `
      <!-- FOOTER -->
      <div style="margin-top: 15px; max-width: 500px; font-family: '${footerSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; color: #666; font-size: 12px; line-height: 1.5; text-align: left;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="table-layout: fixed; text-align: left;">
          <tr>
            <td width="${imageSize}" valign="middle" align="left" style="width: ${imageSize}px; padding-right: 15px; vertical-align: middle;">
              <img src="${footerSettings.footer_image_url}" alt="Logo" width="${imageSize}" height="${imageSize}" border="0" style="display: block; width: ${imageSize}px; height: auto; max-width: ${imageSize}px; border: 0; outline: none; text-decoration: none;" />
            </td>
            <td valign="middle" align="left" style="text-align: left; vertical-align: middle;">
              ${footerSettings.footer_text ? 
                `<div style="margin: 0; font-family: '${footerSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #666; text-align: left;">
                  ${footerSettings.footer_text}
                </div>` : 
                ''}
            </td>
          </tr>
        </table>
        <div style="padding-left: ${iconOffsetLeft}px;">
          ${socialMediaHtml}
        </div>
        ${optOutHtml}
      </div>
    `;
  } else {
    // Standard-Layout mit Bild unter dem Text
    return `
      <!-- FOOTER -->
      <div style="margin-top: 0px; padding-top: 0px; max-width: 500px; font-family: '${footerSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; color: #666; font-size: 12px; line-height: 1.5; text-align: left;">
        ${footerSettings.footer_text ? 
          `<div style="margin: 0; font-family: '${footerSettings.footer_font || "Helvetica Neue"}', Helvetica, Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #666; text-align: left;">
            ${footerSettings.footer_text}
          </div>` : 
          ''}
        ${socialMediaHtml}
        ${footerSettings.footer_image_url ? 
          `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 10px; padding-top: 5px; table-layout: fixed; text-align: left;">
            <tr>
              <td align="left" style="text-align: left;">
                <img src="${footerSettings.footer_image_url}" alt="Logo" width="${imageSize}" height="${imageSize}" border="0" style="display: block; width: ${imageSize}px; height: auto; max-width: ${imageSize}px; border: 0; outline: none; text-decoration: none;" />
              </td>
            </tr>
          </table>` : 
          ''}
        ${optOutHtml}
      </div>
    `;
  }
};

/**
 * Generiert ein standardisiertes HTML-Footer für E-Mails
 * Unterstützt sowohl das alte Single-System als auch das neue Multi-Footer-System
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers
 * @param {string} [footerId] - Optionale Footer-ID um einen bestimmten Footer aus der footerList zu verwenden
 * @returns {string} HTML-Code für den E-Mail-Footer
 */
export const generateEmailFooter = (userSettings, footerId = null) => {
  console.log(`🦶 [FOOTER-GENERATOR] generateEmailFooter called with:`, {
    hasUserSettings: !!userSettings,
    userSettingsType: typeof userSettings,
    userSettingsKeys: userSettings ? Object.keys(userSettings) : [],
    useMultipleFooters: userSettings?.useMultipleFooters,
    footerList: userSettings?.footerList?.length || 0,
    footerText: userSettings?.footer_text,
    footerImageUrl: userSettings?.footer_image_url,
    footerId: footerId
  });

  // Prüfe, ob die notwendigen Einstellungen vorhanden sind
  if (!userSettings) {
    console.warn('🦶 [FOOTER-GENERATOR] No user settings found for footer');
    return '';
  }
  
  // NEUES SYSTEM: Mehrere Footer unterstützen
  if (userSettings.useMultipleFooters === true && Array.isArray(userSettings.footerList) && userSettings.footerList.length > 0) {
    
    // Wenn eine spezifische footerId angegeben ist, nur diesen Footer verwenden
    if (footerId) {
      const specificFooter = userSettings.footerList.find(f => f && f.id === footerId);
      if (specificFooter && specificFooter.isActive !== false) {
        console.log(`🦶 [FOOTER-GENERATOR] Using specific footer for account: ${footerId} ("${specificFooter.name}")`);
        return generateSingleFooter(specificFooter);
      }
      console.warn(`🦶 [FOOTER-GENERATOR] Footer with id "${footerId}" not found or inactive, falling back to first active footer`);
    }
    
    // Kein footerId angegeben oder nicht gefunden: Verwende den ersten aktiven Footer
    console.log(`🦶 [FOOTER-GENERATOR] Using multi-footer system with ${userSettings.footerList.length} footer(s)`);
    const firstActiveFooter = userSettings.footerList.find(footer => footer && footer.isActive !== false);
    if (firstActiveFooter) {
      return generateSingleFooter(firstActiveFooter);
    }
    
    console.warn('🦶 [FOOTER-GENERATOR] No active footers found in footerList');
    return '';
  }
  
  // ALTES SYSTEM: Backward-Kompatibilität - Einzelner Footer
  console.log(`🦶 [FOOTER-GENERATOR] Using legacy single-footer system`);
  return generateSingleFooter(userSettings);
};

/**
 * Fügt einen Footer zu einem E-Mail-Inhalt hinzu
 * @param {string} emailContent - Der E-Mail-Inhalt
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers
 * @param {string} [footerId] - Optionale Footer-ID für account-spezifischen Footer
 * @returns {string} E-Mail-Inhalt mit Footer
 */
export const addFooterToEmail = (emailContent, userSettings = {}, footerId = null) => {
  console.log(`🦶 [FOOTER-GENERATOR] addFooterToEmail called with:`, {
    hasEmailContent: !!emailContent,
    emailContentLength: emailContent?.length,
    hasUserSettings: !!userSettings,
    userSettingsKeys: Object.keys(userSettings || {}),
    footerId: footerId,
    footerText: userSettings?.footer_text,
    footerImageUrl: userSettings?.footer_image_url,
    footerFont: userSettings?.footer_font,
    footerImagePosition: userSettings?.footer_image_position,
    footerImageSize: userSettings?.footer_image_size,
    socialIcons: userSettings?.socialIcons,
    socialIconsPosition: userSettings?.socialIconsPosition,
    socialIconsMarginTop: userSettings?.socialIconsMarginTop,
    socialIconsMarginBottom: userSettings?.socialIconsMarginBottom
  });

  // Prüfe, ob der Inhalt bereits einen Footer hat
  if (emailContent.includes('<!-- FOOTER -->')) {
    console.log(`🦶 [FOOTER-GENERATOR] Footer already exists, returning original content`);
    return emailContent; // Footer bereits vorhanden
  }
  
  // Verwende die generateEmailFooter-Funktion, um den komplexen Footer zu erstellen
  const footerHtml = generateEmailFooter(userSettings, footerId);
  
  console.log(`🦶 [FOOTER-GENERATOR] Generated footer HTML:`, {
    hasFooterHtml: !!footerHtml,
    footerHtmlLength: footerHtml?.length,
    footerHtmlPreview: footerHtml?.substring(0, 200)
  });
  
  // Wenn kein Footer generiert werden konnte, gib den ursprünglichen Inhalt zurück
  if (!footerHtml) {
    console.warn('🦶 [FOOTER-GENERATOR] Could not generate footer. Using default footer.');
    
    // Erstelle einen einfachen Standard-Footer ohne nicht existierende Eigenschaften
    const defaultFooter = `
<!-- FOOTER -->
<div style="margin-top: 20px; border-top: 1px solid #eee; padding-top: 10px; color: #666; font-size: 12px;">
  ${userSettings?.footer_text ? `<p>${userSettings.footer_text}</p>` : '<p>BonusClub</p>'}
</div>
    `.trim();
    
    console.log(`🦶 [FOOTER-GENERATOR] Using default footer:`, defaultFooter);
    
    // Füge den Standard-Footer zum E-Mail-Inhalt hinzu
    const result = `${emailContent}\n\n${defaultFooter}`;
    console.log(`🦶 [FOOTER-GENERATOR] Final result with default footer:`, result?.substring(0, 500));
    return result;
  }
  
  // Füge den generierten Footer zum E-Mail-Inhalt hinzu
  const result = `${emailContent}\n\n${footerHtml}`;
  console.log(`🦶 [FOOTER-GENERATOR] Final result with generated footer:`, result?.substring(0, 500));
  return result;
};

/**
 * Wandelt konservativ Markdown-Emphase (**fett**, _kursiv_) in HTML um.
 * Bewusst restriktiv gehalten, damit vorhandene Inhalte nicht versehentlich
 * formatiert werden: einzelne Sternchen (Listen, Mathe) bleiben unangetastet und
 * Unterstriche innerhalb von Wörtern (snake_case, URLs, E-Mail-Adressen) zählen nicht.
 * @param {string} text
 * @returns {string}
 */
export const applyInlineFormatting = (text) => {
  if (!text) return text;
  return text
    // **fett** – Marker müssen direkt an Nicht-Leerzeichen andocken.
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '<strong>$1</strong>')
    // _kursiv_ – nur an Wortgrenzen, damit snake_case/URLs verschont bleiben.
    .replace(/(^|[\s.,;:!?()"'>*])_(?=\S)([\s\S]*?\S)_(?=$|[\s.,;:!?()"'<*])/g, '$1<em>$2</em>');
};

/**
 * Löst `{{image:<token>}}`-Platzhalter im (bereits gebauten) HTML in echte
 * <img>-Tags auf, die auf ein Image-Lab-Asset zeigen.
 *
 * Bild-Assets werden im "Image Lab" (World Settings) verwaltet und über den
 * Platzhalter `{{image:token}}` im Template referenziert – analog zur
 * bestehenden `{{variable}}`-Konvention. Diese Funktion schreibt den
 * Platzhalter nur auf eine gehostete Storage-URL um; das spam-sichere Inlining
 * (Remote-URL → `cid:`-Attachment) passiert erst beim Versand im Backend
 * (`footerImageEmbed`, das jetzt auch `email_body_images/` matcht).
 *
 * Muss verhaltensgleich zum Backend-Spiegel
 * (`functions/helpers/inlineImageTokens.js`) bleiben.
 *
 * Defensiver Vertrag:
 *   - Keine Tokens vorhanden        → HTML unverändert (Fast-Path).
 *   - Token ohne passendes Asset    → Platzhalter wird ENTFERNT (ein roher
 *     `{{image:...}}`-Token darf niemals beim Empfänger landen).
 *   - Ungültige Eingabe             → Eingabe unverändert, wirft nie.
 *
 * @param {string} html
 * @param {Object} [imageLabAssets] - token -> asset ({ imageUrl, altText, maxWidth, align })
 * @returns {string}
 */
/**
 * Baut aus den User-Settings eine token->asset Map für die Bild-Auflösung.
 * Robust gegen zwei Lade-Varianten:
 *   - `imageLabAssets`  → bereits fertige token->asset Map (EmailAiPopup).
 *   - `imageLab`        → roher RTDB-Knoten assetId->asset (z. B. SmartInbox,
 *                         das den kompletten /settings-Knoten lädt).
 * @param {Object} userSettings
 * @returns {Object} token -> asset
 */
export const buildImageLabMap = (userSettings) => {
  if (!userSettings || typeof userSettings !== 'object') return {};
  if (userSettings.imageLabAssets && typeof userSettings.imageLabAssets === 'object') {
    return userSettings.imageLabAssets;
  }
  const raw = userSettings.imageLab;
  const map = {};
  if (raw && typeof raw === 'object') {
    Object.values(raw).forEach((asset) => {
      if (asset && typeof asset === 'object' && asset.token && asset.imageUrl) {
        map[String(asset.token)] = asset;
      }
    });
  }
  return map;
};

export const resolveImageTokens = (html, imageLabAssets = {}) => {
  if (!html || typeof html !== 'string') return html;
  if (html.indexOf('{{image:') === -1) return html;

  const assets = imageLabAssets && typeof imageLabAssets === 'object' ? imageLabAssets : {};

  const escapeAttr = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  // Height/width ratio (h/w) per aspect — MUST match the backend aspectToSize
  // and functions/helpers/inlineImageTokens.js.
  const ASPECT_RATIO = { square: 1, portrait: 1.5, landscape: 2 / 3 };
  const computeDisplayHeight = (asset, maxWidth) => {
    const w = parseInt(asset && asset.width, 10);
    const h = parseInt(asset && asset.height, 10);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return Math.round(maxWidth * (h / w));
    }
    const ratio = ASPECT_RATIO[asset && asset.aspect];
    return ratio ? Math.round(maxWidth * ratio) : null;
  };

  return html.replace(/\{\{\s*image:([a-zA-Z0-9_-]+)\s*\}\}/g, (_full, rawToken) => {
    const asset = assets[rawToken];
    if (!asset || !asset.imageUrl) return ''; // Unaufgelöste Tokens entfernen
    const parsedWidth = parseInt(asset.maxWidth, 10);
    const maxWidth = Number.isFinite(parsedWidth) ? Math.min(1000, Math.max(50, parsedWidth)) : 600;
    const natural = parseInt(asset && asset.width, 10);
    const displayWidth = Number.isFinite(natural) && natural > 0 ? Math.min(maxWidth, natural) : maxWidth;
    const margin = asset.align === 'left'
      ? '16px 0'
      : asset.align === 'right'
        ? '16px 0 16px auto'
        : '16px auto';
    const alt = escapeAttr(asset.altText || '');
    const src = escapeAttr(asset.imageUrl);
    // Graceful degradation for blocked images (spam folder / images-off): reserve
    // the box via width/height so it doesn't collapse, and style the alt text.
    // No background-color on purpose — it would show through a transparent PNG.
    // displayWidth never exceeds the bitmap, so the client does not scale it up.
    const height = computeDisplayHeight(asset, displayWidth);
    const dimAttrs = `width="${displayWidth}"${height ? ` height="${height}"` : ''}`;
    const img = `<img src="${src}" alt="${alt}" ${dimAttrs} style="display:block;max-width:${displayWidth}px;width:100%;height:auto;margin:${margin};border:0;outline:none;text-decoration:none;color:#5b6b78;font-family:Helvetica,Arial,sans-serif;font-size:13px;line-height:1.45;" />`;
    // Mini-App-Vorschauen verlinken auf die App des Empfängers.
    if (asset.linkUrl && /^https:\/\//i.test(String(asset.linkUrl))) {
      return `<a href="${escapeAttr(asset.linkUrl)}" target="_blank" style="display:block;text-decoration:none;border:0;">${img}</a>`;
    }
    return img;
  });
};

/**
 * Konvertiert Plaintext in HTML mit Absätzen und Zeilenumbrüchen
 * @param {string} text - Der zu konvertierende Text
 * @param {Object} userSettings - Die E-Mail-Einstellungen des Benutzers (optional)
 * @returns {string} HTML-formatierter Text
 */
export const convertToHtml = (text, userSettings = {}, footerId = null) => {
  if (!text) return '';
  
  let fontFamily = 'Helvetica Neue';
  if (userSettings.useMultipleFooters && Array.isArray(userSettings.footerList) && userSettings.footerList.length > 0) {
    let targetFooter = null;
    if (footerId) {
      targetFooter = userSettings.footerList.find(f => f && f.id === footerId);
    }
    if (!targetFooter) {
      targetFooter = userSettings.footerList.find(f => f && f.isActive !== false);
    }
    if (targetFooter?.footer_font) {
      fontFamily = targetFooter.footer_font;
    }
  } else if (userSettings.footer_font) {
    fontFamily = userSettings.footer_font;
  }
  
  // Ersetze Zeilenumbrüche durch <br> oder <p> Tags
  const withParagraphs = text
    .split('\n\n') // Leere Zeilen als Absatzgrenzen verwenden
    .map(paragraph => paragraph.trim())
    .filter(paragraph => paragraph.length > 0)
    .map(paragraph => `<p style="font-family: '${fontFamily}', Helvetica, Arial, sans-serif;">${applyInlineFormatting(paragraph)}</p>`)
    .join('');
    
  // Ersetze einzelne Zeilenumbrüche innerhalb von Absätzen durch <br>
  const withLineBreaks = withParagraphs.replace(/\n/g, '<br>');
  
  // Packe alles in ein div mit der Schriftart
  const wrapped = `<div style="font-family: '${fontFamily}', Helvetica, Arial, sans-serif; color: #333; font-size: 16px; line-height: 1.6;">${withLineBreaks}</div>`;

  // Image-Lab-Platzhalter ({{image:token}}) auflösen. Läuft NACH convertToHtml,
  // damit das injizierte <img> nicht durch applyInlineFormatting verändert wird.
  // Immer aufrufen: löst bei vorhandenen Assets auf und entfernt sonst den
  // Platzhalter (Sicherheitsnetz, damit nie ein roher Token versendet wird).
  return resolveImageTokens(wrapped, buildImageLabMap(userSettings));
}; 